import { type SavedCopy, type SavePreview, type SaveResult, saveReplenishmentInstruments } from '@api/replenishment-engine';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { Alert, Button, Group, SegmentedControl, Stack, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useMemo, useState, type FC, type ReactElement } from 'react';

import ExplainWindow from '../ExplainWindow';
import { dayLabel } from './dates';
import { TYPE_LABEL } from './status';
import { Lines } from './tables';

const n = (v: number | null | undefined): string => (v == null ? '—' : formatCount(v, { maxDecimals: 0 }));

type Line = SavePreview['lines'][number];

type Props = {
    preview: SavePreview;
    ids: string[];
    objectiveId?: string;
    period: { start: string | null; end: string | null };
    onClose: () => void;
    onDone: (result: SaveResult) => void;
};

const readError = (e: unknown): string => {
    const err = e as { error?: { message?: string }; message?: string };
    return err?.error?.message ?? err?.message ?? 'Something went wrong';
};

const savedOn = (s: SavedCopy | null): string => (s?.savedAt ? `saved ${dayLabel(s.savedAt.slice(0, 10))}` : 'saved');

/**
 * Saving again after a re-run: each line against the plan already saved for this objective
 * and period. The same action (SKU, item, vendor, day) is never saved twice: with the same
 * number it is left as it is, with a new number the brand keeps the saved one or takes the
 * new one, and a saved line the run no longer proposes is kept or withdrawn. Numbers on the
 * plan are not touched either way; a run never reads saved lines.
 */
const SaveReviewSheet: FC<Props> = ({ preview, ids, objectiveId, period, onClose, onDone }) => {
    const changed = preview.lines.filter((l) => l.match === 'CHANGED');
    const same = preview.lines.filter((l) => l.match === 'SAME');
    const fresh = preview.lines.filter((l) => l.match === 'NEW');
    const [replace, setReplace] = useState<Set<string>>(new Set());
    const [withdraw, setWithdraw] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const toggle = (set: Set<string>, id: string, on: boolean): Set<string> => {
        const next = new Set(set);
        if (on) next.add(id);
        else next.delete(id);
        return next;
    };

    const what = (r: { sku: string; type: Line['type']; itemName: string | null; vendorName: string | null }): ReactElement => <Lines lines={[{ text: r.itemName ?? r.sku, strong: true }, { text: '', sub: `${TYPE_LABEL[r.type] ?? r.type}${r.itemName && r.itemName !== r.sku ? ` · for ${r.sku}` : ''}${r.vendorName ? ` · ${r.vendorName}` : ''}` }]} />;

    const changedColumns = useMemo<DataSheetColumn<Line>[]>(
        () => [
            { key: 'what', header: 'Line', width: 240, render: (_v, l): ReactElement => what(l) },
            { key: 'day', header: 'Day', width: 90, render: (_v, l): ReactElement => <Text size="sm">{dayLabel(l.placeBy)}</Text> },
            { key: 'saved', header: 'Saved', width: 120, align: 'right', render: (_v, l): ReactElement => <Lines lines={[{ text: n(l.saved?.qty), strong: true, sub: savedOn(l.saved) }]} /> },
            { key: 'now', header: 'Plan now', width: 120, align: 'right', render: (_v, l): ReactElement => <Lines lines={[{ text: n(l.qty), strong: true, sub: `${l.qty > (l.saved?.qty ?? 0) ? '+' : ''}${n(l.qty - (l.saved?.qty ?? 0))}`, tone: 'orange' }]} /> },
            { key: 'done', header: 'Done so far', width: 110, align: 'right', render: (_v, l): ReactElement => <Text size="sm">{n(l.saved?.doneQty)}</Text> },
            {
                key: 'pick',
                header: '',
                width: 200,
                render: (_v, l): ReactElement => (
                    <SegmentedControl size="xs" value={l.saved && replace.has(l.saved.id) ? 'new' : 'keep'} onChange={(v) => l.saved && setReplace((cur) => toggle(cur, l.saved!.id, v === 'new'))} data={[{ value: 'keep', label: 'Keep saved' }, { value: 'new', label: 'Take new' }]} />
                ),
            },
        ],
        [replace],
    );
    const droppedColumns = useMemo<DataSheetColumn<SavedCopy>[]>(
        () => [
            { key: 'what', header: 'Line', width: 240, render: (_v, s): ReactElement => what(s) },
            { key: 'day', header: 'Day', width: 90, render: (_v, s): ReactElement => <Text size="sm">{dayLabel(s.placeBy)}</Text> },
            { key: 'saved', header: 'Saved', width: 120, align: 'right', render: (_v, s): ReactElement => <Lines lines={[{ text: n(s.qty), strong: true, sub: savedOn(s) }]} /> },
            { key: 'done', header: 'Done so far', width: 110, align: 'right', render: (_v, s): ReactElement => <Text size="sm">{n(s.doneQty)}</Text> },
            {
                key: 'pick',
                header: '',
                width: 200,
                render: (_v, s): ReactElement => <SegmentedControl size="xs" value={withdraw.has(s.id) ? 'withdraw' : 'keep'} onChange={(v) => setWithdraw((cur) => toggle(cur, s.id, v === 'withdraw'))} data={[{ value: 'keep', label: 'Keep saved' }, { value: 'withdraw', label: 'Withdraw' }]} />,
            },
        ],
        [withdraw],
    );
    const sameColumns = useMemo<DataSheetColumn<Line>[]>(
        () => [
            { key: 'what', header: 'Line', width: 240, render: (_v, l): ReactElement => what(l) },
            { key: 'day', header: 'Day', width: 90, render: (_v, l): ReactElement => <Text size="sm">{dayLabel(l.placeBy)}</Text> },
            { key: 'qty', header: 'Qty', width: 120, align: 'right', render: (_v, l): ReactElement => <Lines lines={[{ text: n(l.qty), strong: true, sub: savedOn(l.saved) }]} /> },
            { key: 'done', header: 'Done so far', width: 110, align: 'right', render: (_v, l): ReactElement => <Text size="sm">{n(l.saved?.doneQty)}</Text> },
        ],
        [],
    );

    const writes = fresh.length + replace.size + withdraw.size;
    const confirm = async (): Promise<void> => {
        setBusy(true);
        setError(null);
        try {
            onDone(await saveReplenishmentInstruments(ids, objectiveId, period, { replaceSavedIds: [...replace], withdrawSavedIds: [...withdraw] }));
        } catch (e) {
            setError(readError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <ExplainWindow
            opened
            wide
            onClose={onClose}
            title="This plan is already saved"
            subtitle={`${period.start && period.end ? `${dayLabel(period.start)} → ${dayLabel(period.end)} · ` : ''}${fresh.length} new · ${same.length} unchanged · ${changed.length} changed · ${preview.dropped.length} no longer proposed`}
            footer={
                <Group justify="space-between" p="sm">
                    <Text size="xs" c="dimmed">
                        Saved lines are a record to compare with actuals. The plan's numbers don't change either way.
                    </Text>
                    <Group gap="xs">
                        <Button size="xs" variant="default" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button size="xs" color="grape" onClick={() => void confirm()} loading={busy} disabled={writes === 0}>
                            {writes === 0 ? 'Nothing to change' : `Save ${fresh.length} new${replace.size ? ` · replace ${replace.size}` : ''}${withdraw.size ? ` · withdraw ${withdraw.size}` : ''}`}
                        </Button>
                    </Group>
                </Group>
            }
        >
            <Stack gap="md" p="md">
                {error && (
                    <Alert color="red" withCloseButton onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {changed.length > 0 && (
                    <Stack gap={6}>
                        <Text size="sm" fw={700}>
                            Changed since you saved ({changed.length})
                        </Text>
                        <DataSheet<Line> data={changed} columns={changedColumns} getRowKey={(l) => l.instrumentId} rowHeight={48} bare hideHeaderBar />
                    </Stack>
                )}
                {preview.dropped.length > 0 && (
                    <Stack gap={6}>
                        <Text size="sm" fw={700}>
                            Saved, but the run no longer proposes them ({preview.dropped.length})
                        </Text>
                        <Text size="xs" c="dimmed">
                            Usually stock or in-production moved since the save. Check Done so far before you withdraw.
                        </Text>
                        <DataSheet<SavedCopy> data={preview.dropped} columns={droppedColumns} getRowKey={(s) => s.id} rowHeight={48} bare hideHeaderBar />
                    </Stack>
                )}
                {same.length > 0 && (
                    <Stack gap={6}>
                        <Text size="sm" fw={700}>
                            Already saved with the same numbers ({same.length}), left as they are
                        </Text>
                        <DataSheet<Line> data={same} columns={sameColumns} getRowKey={(l) => l.instrumentId} rowHeight={48} bare hideHeaderBar />
                    </Stack>
                )}
            </Stack>
        </ExplainWindow>
    );
};

export default SaveReviewSheet;
